/**
 * Form Tool Generator — walks UI tree to find form elements,
 * groups them by nearest parent container, and generates submit tools.
 */

import type { CogoElement, CogoBlockDef } from "@cogo/types"
import type { WebMCPTool, JsonSchema } from "../core/tool-registry"
import type { StateManagerLike } from "../core/state-provider"

// ── Types ──────────────────────────────────────────────────

export interface ActionEngineLike {
  executeFlow(flowName: string, context?: Record<string, unknown>): Promise<unknown>
}

export interface GeneratorRuntime {
  stateManager: StateManagerLike
  actionEngine: ActionEngineLike
}

/** Types considered form input elements */
const FORM_INPUT_TYPES = new Set([
  "input",
  "textarea",
  "select",
  "checkbox",
  "radio",
  "switch",
  "slider",
])

/** Types considered form containers */
const FORM_CONTAINER_TYPES = new Set(["form", "container", "card", "section"])

// ── Field extraction ───────────────────────────────────────

export interface FormField {
  elementId: string
  name: string
  label: string
  type: string       // JSON Schema type: string, boolean, number
  inputType: string  // Cogo element type: input, checkbox, select, etc.
  required: boolean
  placeholder?: string
  bindPath?: string  // #{ui.xxx} bind target
  enumValues?: string[]
}

function inferFieldName(el: CogoElement): string {
  // Try: explicit name > id-derived > label-derived > fallback
  if (el.name) return el.name
  if (el.bind) {
    // #{ui.fieldName} → fieldName
    const match = /^#\{(?:ui\.)?([^}]+)\}$/.exec(el.bind)
    if (match) return match[1]
  }
  if (el.value && typeof el.value === "string") {
    const match = /^#\{(?:ui\.)?([^}]+)\}$/.exec(el.value)
    if (match) return match[1]
  }
  // Derive from element id: "input-name" → "name", "field-email" → "email"
  const id = el.id
  const cleaned = id.replace(/^(input|field|select|checkbox|radio|switch|slider)-/, "")
  return cleaned
}

function inferFieldLabel(el: CogoElement, siblings: CogoElement[]): string {
  // Check if there's a label sibling
  if (el.label) return el.label
  for (const sib of siblings) {
    if (sib.type === "text" && sib.tag === "label" && sib.text) {
      // Strip trailing markers like " *"
      return sib.text.replace(/\s*\*\s*$/, "").replace(/^#\{[^}]+\}$/, sib.id)
    }
  }
  // Fall back to humanized field name
  const name = inferFieldName(el)
  return name
    .replace(/([a-z])([A-Z])/g, "$1 $2")
    .replace(/[_-]/g, " ")
    .replace(/\b\w/g, (c) => c.toUpperCase())
}

function mapInputToSchemaType(el: CogoElement): string {
  switch (el.type) {
    case "checkbox":
    case "switch":
      return "boolean"
    case "slider":
      return "number"
    case "input":
      if (el.inputType === "number" || el.inputType === "range") return "number"
      return "string"
    default:
      return "string"
  }
}

function extractBindPath(el: CogoElement): string | undefined {
  if (el.bind) return el.bind
  if (el.value && typeof el.value === "string" && el.value.startsWith("#{")) {
    return el.value
  }
  return undefined
}

// ── Tree walking ───────────────────────────────────────────

interface FormGroup {
  containerId: string
  containerLabel: string
  fields: FormField[]
  submitFlowName?: string
}

function findFormGroups(root: CogoElement): FormGroup[] {
  const groups: FormGroup[] = []

  function walkContainer(el: CogoElement, parentGroup: FormGroup | null): void {
    const children = el.children ?? []
    const isFormLike =
      el.type === "form" ||
      (FORM_CONTAINER_TYPES.has(el.type) && children.some((c) => FORM_INPUT_TYPES.has(c.type) ||
        (c.children ?? []).some((gc) => FORM_INPUT_TYPES.has(gc.type))))

    let currentGroup = parentGroup

    if (isFormLike && !parentGroup) {
      currentGroup = {
        containerId: el.id,
        containerLabel: el.id,
        fields: [],
        submitFlowName: undefined,
      }
      groups.push(currentGroup)
    }

    for (const child of children) {
      if (FORM_INPUT_TYPES.has(child.type)) {
        const siblings = children
        const field: FormField = {
          elementId: child.id,
          name: inferFieldName(child),
          label: inferFieldLabel(child, siblings),
          type: mapInputToSchemaType(child),
          inputType: child.type,
          required: child.required === true || child.required === "true",
          placeholder: child.placeholder,
          bindPath: extractBindPath(child),
        }
        if (currentGroup) {
          currentGroup.fields.push(field)
        } else {
          // Orphan input — create a solo group
          const soloGroup: FormGroup = {
            containerId: el.id,
            containerLabel: el.id,
            fields: [field],
          }
          groups.push(soloGroup)
        }
      } else if (child.type === "button" && child.events) {
        // Detect submit button
        const tapEvent = child.events.tap
        if (tapEvent && currentGroup) {
          const flowName = typeof tapEvent === "string" ? tapEvent : tapEvent.flow
          if (flowName && /submit|save|create|update/i.test(flowName)) {
            currentGroup.submitFlowName = flowName
          }
        }
      } else if (child.children) {
        // Recurse into nested containers, inheriting form context
        walkContainer(child, currentGroup)
      }
    }
  }

  walkContainer(root, null)

  // If no groups found, try deeper scan: collect all inputs under root
  if (groups.length === 0) {
    const allInputs: FormField[] = []
    let submitFlow: string | undefined

    function deepScan(el: CogoElement, parentChildren: CogoElement[]): void {
      if (FORM_INPUT_TYPES.has(el.type)) {
        allInputs.push({
          elementId: el.id,
          name: inferFieldName(el),
          label: inferFieldLabel(el, parentChildren),
          type: mapInputToSchemaType(el),
          inputType: el.type,
          required: el.required === true || el.required === "true",
          placeholder: el.placeholder,
          bindPath: extractBindPath(el),
        })
      }
      if (el.type === "button" && el.events?.tap) {
        const tapEvent = el.events.tap
        const flowName = typeof tapEvent === "string" ? tapEvent : tapEvent.flow
        if (flowName && /submit|save|create|update|validate/i.test(flowName)) {
          submitFlow = flowName
        }
      }
      for (const child of el.children ?? []) {
        deepScan(child, el.children ?? [])
      }
    }

    deepScan(root, [])

    if (allInputs.length > 0) {
      groups.push({
        containerId: root.id,
        containerLabel: root.id,
        fields: allInputs,
        submitFlowName: submitFlow,
      })
    }
  }

  return groups
}

// ── Tool generation ────────────────────────────────────────

function formGroupToInputSchema(group: FormGroup): JsonSchema {
  const properties: Record<string, JsonSchema> = {}
  const required: string[] = []

  for (const field of group.fields) {
    properties[field.name] = {
      type: field.type,
      description: field.label,
    }
    if (field.placeholder) {
      properties[field.name].description += ` (${field.placeholder})`
    }
    if (field.enumValues && field.enumValues.length > 0) {
      properties[field.name].enum = field.enumValues
    }
    if (field.required) {
      required.push(field.name)
    }
  }

  return {
    type: "object",
    properties,
    ...(required.length > 0 ? { required } : {}),
  }
}

function sanitizeToolName(raw: string): string {
  return raw
    .replace(/[^a-zA-Z0-9_-]/g, "_")
    .replace(/_+/g, "_")
    .replace(/^_|_$/g, "")
    .toLowerCase()
}

export function generateFormTools(
  block: CogoBlockDef,
  narratives?: Record<string, { effect: string }>,
): WebMCPTool[] {
  if (!block.root) return []
  const groups = findFormGroups(block.root)
  const tools: WebMCPTool[] = []

  for (const group of groups) {
    if (group.fields.length === 0) continue

    const toolName = `submit_${sanitizeToolName(group.containerId)}`
    const inputSchema = formGroupToInputSchema(group)

    tools.push({
      name: toolName,
      description: narratives?.[toolName]?.effect
        ?? (group.submitFlowName && narratives?.[group.submitFlowName]?.effect)
        ?? `Submit form "${group.containerLabel}" with ${group.fields.length} field(s): ${group.fields.map((f) => f.name).join(", ")}`,
      inputSchema,
      handler: createFormHandler(group),
      metadata: {
        blockName: block.blockType,
        readOnly: false,
        tags: ["form", "submit"],
      },
    })
  }

  return tools
}

function createFormHandler(
  group: FormGroup,
): (params: Record<string, unknown>) => (runtime: GeneratorRuntime) => Promise<unknown> {
  return (params: Record<string, unknown>) => {
    return async (runtime: GeneratorRuntime) => {
      // Set each field's state
      for (const field of group.fields) {
        if (field.bindPath && params[field.name] !== undefined) {
          // #{ui.fieldName} → ui.fieldName
          const path = field.bindPath.replace(/^#\{/, "").replace(/\}$/, "")
          runtime.stateManager.setState(path, params[field.name])
        }
      }

      // Execute submit flow if detected
      if (group.submitFlowName) {
        return runtime.actionEngine.executeFlow(group.submitFlowName, params)
      }

      return { success: true, fieldsSet: Object.keys(params) }
    }
  }
}
